import { Close, Visibility, PhotoCameraRounded } from '@mui/icons-material';
import clsx from 'clsx';
import { Box, Button, Fade, FormLabel, Grid, IconButton, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useEffect, useState } from 'react';
import { Modal } from '../modal';
import { useSnackbar } from 'notistack';
import { imageFileTypes } from './utils';

interface IImageUpload {
  handleFileChange: (val: string, file?: File) => void;
  image?: string;
  disabled?: boolean;
  isRequired?: boolean;
  showSource?: boolean;
  labelClassName?: string;
  imageKey?: string;
  showLabel?: boolean;
  canRemoveImage?: boolean;
  canOverride?: boolean;
  iconUploadButtonSize?: string;
  disableIconButtonPadding?: boolean;
}

export const ImageModal = ({ isOpen, onClose, source }) => {
  const classes = useStyles();
  return (
    <Modal
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <Fade in={isOpen}>
        <Grid container className={classes.root}>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center" className={classes.imgBox}>
              <img src={source} alt={'snap'} className={classes.img}></img>
            </Box>
          </Grid>
        </Grid>
      </Fade>
    </Modal>
  );
};

export const ImageUpload: FC<IImageUpload> = ({
  imageKey,
  handleFileChange,
  image,
  disabled = false,
  isRequired = false,
  showSource = true,
  showLabel = true,
  labelClassName,
  canRemoveImage = true,
  canOverride,
  iconUploadButtonSize = 'large',
  disableIconButtonPadding = false
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [source, setSource] = useState<string>('');
  const [isShowingImage, showImage] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>('image.png');

  useEffect(() => {
    setSource(image);
  }, [image]);

  const createBase64Image = async file => {
    const reader = new FileReader();
    return new Promise(function (resolve, reject) {
      reader.onload = function (event) {
        resolve(event.target.result);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleCapture = async target => {
    if (target.files) {
      if (target.files.length !== 0) {
        const file = target.files[0];
        // file.type is in the format of 'image/png' or 'application/pdf' or 'text/csv'
        const uploadedFileType = file.type.split?.('/')?.[1];
        if (!file.type.includes('image')) {
          return enqueueSnackbar(
            `Invalid file type, ${uploadedFileType}. Please upload one of these image types, ${imageFileTypes.join(
              ', '
            )}.`,
            {
              variant: 'error'
            }
          );
        }
        const base64 = await createBase64Image(target.files[0]);
        const newUrl = URL.createObjectURL(file);
        handleFileChange(base64 as string, file);
        setSource(newUrl);
        setFileName(file.name);
      }
    }
  };

  return (
    <Grid container className={classes.root}>
      <Grid item xs={12}>
        {showLabel && (
          <FormLabel required={isRequired} className={clsx(classes.heading, labelClassName)}>
            Upload Image
          </FormLabel>
        )}
        {isRequired && !source && (
          <Typography variant="caption" className={classes.imageHelpText}>
            Uploading an image is required.
          </Typography>
        )}

        {source && showSource && (
          <>
            <Box display="flex">
              <Button startIcon={<Visibility />} color="primary" onClick={() => showImage(true)}>
                {fileName}
              </Button>
              {canRemoveImage && (
                <Button
                  onClick={() => {
                    const result = window.confirm('Are you sure you want to remove this image?');
                    if (result) {
                      setSource('');
                      handleFileChange('');
                    }
                  }}
                  className={classes.removeButton}
                  startIcon={<Close />}
                >
                  Remove Image
                </Button>
              )}
            </Box>
            <Box marginTop="8px">
              <Typography component="p" variant="caption">
                Click the file name to view the whole image.
              </Typography>
            </Box>
          </>
        )}
        <input
          accept="image/*"
          disabled={disabled}
          className={classes.input}
          id={`icon-button-file-${imageKey || ''}`}
          type="file"
          onChange={e => {
            handleCapture(e.target);
            // reset the input value so you can re-upload the same photo if you want to
            e.target.value = '';
          }}
        />

        {(canOverride || !source || !showSource) && (
          <Box marginLeft={showLabel ? '1rem' : 0}>
            <label htmlFor={`icon-button-file-${imageKey || ''}`}>
              <IconButton
                color="primary"
                disabled={disabled}
                aria-label="upload picture"
                component="span"
                sx={{ padding: disableIconButtonPadding ? 0 : undefined }}
              >
                <PhotoCameraRounded
                  fontSize={iconUploadButtonSize as any}
                  color={disabled ? 'disabled' : 'primary'}
                />
              </IconButton>
            </label>
          </Box>
        )}
      </Grid>
      <ImageModal isOpen={isShowingImage} source={source} onClose={() => showImage(false)} />
    </Grid>
  );
};

const useStyles = makeStyles(theme => ({
  root: {},
  heading: {
    marginTop: theme.spacing(1),
    display: 'block'
  },
  imgBox: {
    maxWidth: '100%'
  },
  img: {
    height: 'inherit',
    maxWidth: 'inherit',
    padding: theme.spacing(1)
  },
  input: {
    display: 'none'
  },
  removeButton: {
    color: theme.palette.error.main
  },
  imageHelpText: {
    color: theme.palette.error.main
  }
}));
